import { TeamMember } from "@/lib/types";

export const teamMembers: TeamMember[] = [
  {
    id: "01",
    slug: "muntahi-safwan",
    name: "Muntahi Safwan",
    designation: "Founder & Chief Deadline Negotiator",
    tagline: "Big ideas, shipped before the deadline blinks.",
    image: "/assets/images/teams/muntahi.jpeg",
    bio: "Muntahi founded Octarnal to prove that design, engineering and business sense can live under one roof. He steers the vision, keeps every project on schedule, and dives into any part of the stack when it counts.",
    longBio: [
      "Muntahi is a generalist by design — equally comfortable pitching a client, sketching a flow, or reviewing a pull request at midnight. He started Octarnal with a stubborn belief that great design and sharp engineering should never live in separate rooms, and he has spent every day since trying to prove it. Before the studio had a name, it was him and a laptop, taking on projects most people said were too ambitious for a small team. He learned early that a founder's real job is not to have all the answers, but to keep the room moving toward them. On any given day he might be writing a proposal in the morning, unblocking a developer at noon, and redrawing a landing page at night. He treats the first three seconds of every experience as sacred, because he knows that is where people decide whether to care.",
      "He is the one who says 'ship it' when the team is stuck polishing, and the one who says 'not yet' when they are about to cut a corner that matters. Deadlines, to him, are not walls but conversations — hence the title. He would rather promise less and deliver more than dazzle a client and disappoint them later. He reads the room better than he reads code, though he does plenty of both. He believes momentum, not perfection, is what makes early work feel alive. He keeps the team small on purpose, because he trusts a handful of obsessed people over a crowd of comfortable ones. He is fiercely protective of the people who work with him and will absorb pressure so they can stay in flow. He measures a project less by how it looks on launch day and more by whether the client comes back. He keeps a running note of ideas he is not ready to build yet, and revisits it more often than he admits. When something breaks in production, he is calm in a way that calms everyone else.",
      "He believes culture is just the worst behavior a leader is willing to tolerate, so he sets the bar high and stands on it himself. He is allergic to meetings that could have been a message. He is still, at heart, the kid who wanted to make things that felt like magic — he just runs a company now. He came up self-taught, stitching together skills from tutorials, failed side projects, and clients who took a chance on him. That path left him with an unusual respect for people who are still figuring it out. He hires for hunger over pedigree, because hunger is the one thing he cannot teach. He is comfortable being the least technical person in a room full of engineers and the most technical in a room full of clients. He sees pricing, positioning, and product as the same conversation viewed from different chairs. He has walked away from money that came with the wrong expectations, and he has never regretted it. He guards the studio's reputation like it is the only asset that cannot be rebuilt, because it nearly is. He is a compulsive note-taker, with scattered documents full of half-formed ideas and product bets. He believes a founder sets the weather, so he tries to walk in calm even on the hard days. He celebrates the team's wins louder than his own and takes the blame quieter than anyone. He wants Octarnal to outgrow its dependence on him, and he is slowly, deliberately building toward that. Ask him what he does and he will shrug and say 'whatever the work needs that day' — and he means it. Octarnal is his answer to a simple question he keeps asking: what would happen if a small team refused to choose between beautiful and functional?",
    ].join("\n\n"),
    experience: "5+ Years",
    location: "Dhaka, BD",
    techStack: [
      // Important — core frameworks, languages & databases (rendered larger)
      "React",
      "Next.js",
      "TypeScript",
      "JavaScript",
      "Node.js",
      "Python",
      "PostgreSQL",
      "MongoDB",
      "PHP",
      "Laravel",
      // Supporting — libraries, styling, tooling
      "Express",
      "NestJS",
      "GraphQL",
      "Redis",
      "AI",
      "Automation",
      "RAG",
      "Chatbot",
      "Docker",
      "Kubernetes",
      "AWS",
      "Tailwind",
      "HTML",
      "CSS",
      "Vercel",
      "Firebase",
      "Postman",
      "GSAP",
      "Figma",
      "C",
      "C++",
    ],
    expertise: ["Product Vision", "Team Leadership", "Full-Stack"],
    funFacts: [
      "Has never met a deadline he couldn't renegotiate.",
      "Runs on coffee, roadmaps and stubborn optimism.",
    ],
    uniqueItems: [
      { label: "Projects Shipped", value: "40+" },
      { label: "All-Nighters", value: "∞" },
      { label: "Roles Juggled", value: "7" },
    ],
    socials: [
      {
        platform: "LinkedIn",
        href: "https://www.linkedin.com/in/muntahi-safwan/",
      },
      {
        platform: "Facebook",
        href: "https://www.facebook.com/muntahi.safwan.1",
      },
    ],
    accentColor: "#b2d127",
    quote: "Ship it, then make it better — but ship it.",
    qa: [
      { q: "Ship or polish?", a: "Ship, then polish in public." },
      { q: "Meeting or message?", a: "Message. Always the message." },
      { q: "Coffee or roadmap?", a: "Coffee, then roadmap, then more coffee." },
      { q: "Hire for?", a: "Hunger over pedigree, every time." },
      { q: "A deadline is?", a: "A conversation, not a wall." },
    ],
    endorsements: [
      {
        by: "Sagor Hossain",
        role: "Co-Founder",
        quote:
          "Muntahi will renegotiate a deadline with a straight face and somehow make the client thank him for it.",
      },
      {
        by: "Abdul M. Fahim",
        role: "CTO",
        quote:
          "Least technical person in an engineering room, and he still asks the one question that changes the build.",
      },
      {
        by: "Shaqibul Neil",
        role: "Full Stack",
        quote:
          "He said 'ship it' once and I learned more in that hour than in a month of polishing.",
      },
    ],
    metadata: {
      title: "Muntahi Safwan — Founder",
      description:
        "Meet Muntahi Safwan, Founder of Octarnal — steering product vision, leading the team, and shipping across the full stack.",
      keywords: ["Founder", "Product Vision", "Full-Stack", "Octarnal Team"],
    },
  },
  {
    id: "02",
    slug: "sagor-hossain",
    name: "Sagor Hossain",
    designation: "Co-Founder & Professional Problem Collector",
    tagline: "Every problem is a product waiting to happen.",
    image: "/assets/images/teams/sagor.png",
    bio: "Sagor co-founded Octarnal and made a habit of collecting the problems nobody else wanted to touch. He turns messy client requirements into clear scopes and keeps the whole operation moving.",
    longBio: [
      "Sagor sits exactly where strategy meets delivery, and he has made that uncomfortable middle ground his home. He co-founded Octarnal because he was tired of watching good ideas die in the gap between what a client asked for and what actually got built. He is the person who reads a vague brief three times, asks the five questions nobody wanted to ask, and comes back with a scope everyone can finally agree on. Problems do not scare him — he collects them, files them, and works through them one by one until the pile is smaller than it was yesterday. He has a gift for translating between people who do not speak the same language: founders and engineers, designers and accountants, ambition and budget. On a normal day he is on a client call in the morning, rewriting a timeline at lunch, and debugging something himself in the afternoon because it was faster than explaining it. He believes the fastest way to solve a problem is to first say out loud, plainly, exactly what it is. He is suspicious of solutions that arrive before the problem is fully understood. He keeps the operation moving when everyone else is heads-down, quietly removing obstacles before they become excuses. He would rather have a hard conversation early than a disappointed client late. He tracks the things that fall through the cracks, because he has learned that most failures are just small things nobody owned.",
      "He is calm under pressure in a way that makes clients trust the whole team more. He does not chase credit; he chases closure. He has a running list of problems 'for later' that never seems to shrink, and he has made peace with that. He believes a good scope is a kind of promise, and he does not make promises lightly. He is the reason projects that should have spiraled somehow stayed on the rails. He learns a client's business fast enough to argue for it in the room. He is happiest when a messy, tangled engagement finally clicks into something clean and shippable. He and Muntahi are two halves of the same instinct — one pushes the vision, the other makes sure it survives contact with reality. He grew up negotiating — with siblings, with teachers, with his own impatience — long before he ever negotiated a contract. He is the person clients call when something has gone wrong, precisely because he does not flinch.",
      "He treats a difficult conversation as a service, not a confrontation. He keeps meticulous notes so that no promise made in a call ever quietly disappears. He has a sixth sense for scope creep and heads it off before it becomes resentment. He believes a project's health is decided in the first week, in the questions you are brave enough to ask. He is equally happy in a spreadsheet, a codebase, or a hard client conversation, which is rarer than it sounds. He measures his own performance by how few surprises the team gets, not how busy he looks. He is generous with context, because he has seen how much damage a single unshared detail can do. He protects the makers' focus by absorbing the noise that would otherwise reach them. He would rather be the reason a project shipped on time than the reason it looked impressive in a pitch. He treats every solved problem as one less thing that can hurt a client later. If Octarnal feels organized from the outside, it is usually because Sagor absorbed the chaos on the inside.",
    ].join("\n\n"),
    experience: "5+ Years",
    location: "Dhaka, BD",
    techStack: [
      // Important — core frameworks, languages & databases
      "React",
      "Next.js",
      "TypeScript",
      "JavaScript",
      "Node.js",
      "Python",
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "PHP",
      // Supporting — libraries, styling, tooling
      "Laravel",
      "Express",
      "NestJS",
      "GraphQL",
      "Redis",
      "AI",
      "Automation",
      "RAG",
      "Chatbot",
      "Docker",
      "Tailwind",
      "HTML",
      "CSS",
      "Vercel",
      "Firebase",
      "Postman",
      "Notion",
      "Figma",
      "C",
      "C++",
    ],
    expertise: ["Operations", "Client Strategy", "Full-Stack"],
    funFacts: [
      "Keeps a running list of problems 'for later' that never shrinks.",
      "Solves his best bugs in the shower.",
    ],
    uniqueItems: [
      { label: "Problems Solved", value: "1k+" },
      { label: "Scopes Written", value: "120" },
      { label: "Meetings Survived", value: "∞" },
    ],
    socials: [
      {
        platform: "LinkedIn",
        href: "https://www.linkedin.com/in/sagor-hossain-web-dev/",
      },
      {
        platform: "Facebook",
        href: "https://www.facebook.com/sagor.hossain.407337",
      },
    ],
    accentColor: "#7c9cff",
    quote: "Name the problem out loud and it's already half-solved.",
    qa: [
      {
        q: "Problem or solution first?",
        a: "Name the problem out loud first.",
      },
      {
        q: "Spreadsheet or codebase?",
        a: "Whichever unblocks the team faster.",
      },
      { q: "Scope creep?", a: "Caught in week one or never." },
      { q: "Credit or closure?", a: "Closure. Every time." },
      { q: "Hard talk?", a: "Early and honest beats late and sorry." },
    ],
    endorsements: [
      {
        by: "Muntahi Safwan",
        role: "Founder",
        quote:
          "I push the vision; Sagor makes sure it survives contact with reality. I'd be lost without the second half.",
      },
      {
        by: "Meraz Uddin",
        role: "DevOps",
        quote:
          "He turns a vague brief into a scope so clean you forget how messy it started.",
      },
      {
        by: "Mahfuzul Hasan",
        role: "Frontend",
        quote:
          "Sagor asks the five questions nobody wanted to ask, and every one of them saves the project.",
      },
    ],
    metadata: {
      title: "Sagor Hossain — Co-Founder",
      description:
        "Meet Sagor Hossain, Co-Founder of Octarnal — turning tangled requirements into clear scopes and shipped products.",
      keywords: ["Co-Founder", "Operations", "Strategy", "Octarnal Team"],
    },
  },
  {
    id: "03",
    slug: "abdul-m-fahim",
    name: "Abdul M. Fahim",
    designation: "CTO, Backend Maestro & Chief Schema Whisperer",
    tagline: "If it runs at scale, it runs on his rules.",
    image: "/assets/images/teams/fahim.jpg",
    bio: "Fahim owns everything behind the curtain. As CTO and backend maestro he designs the databases, APIs and infrastructure that keep our products fast, secure and always up.",
    longBio: [
      "Fahim treats the backend like an orchestra — every service in time, nothing out of place, and no soloist louder than the song. As Octarnal's CTO he owns the parts of the product nobody sees but everybody depends on: the databases, the APIs, the auth, the infrastructure that has to be awake at three in the morning. He has scaled systems from their first request to their millionth, and he treats every millisecond of latency as a personal insult. He designs data models the way an architect designs a building — assuming it will still be standing, and growing, ten years from now. He is convinced that most 'frontend' bugs are really backend decisions in disguise, and he is usually right. He sets the engineering standards the rest of the team builds on, and he holds the line on them even when it is inconvenient. He would rather spend an extra day on a clean schema than an extra month untangling a messy one later. He reads other people's code carefully, because he knows a good review teaches more than a lecture. He is calm during outages in a way that comes only from having survived a lot of them. He believes security is not a feature you add at the end but a habit you keep from the start. He automates the boring parts so his team can spend their attention where it counts.",
      "He can name the time complexity of a function before you finish typing it, and he will tell you kindly. He thinks in schemas and dreams in indexes, and he is not even a little embarrassed about it. He is the person who asks 'but what happens at scale?' before anyone else has thought to. He mentors the younger engineers patiently, because he remembers being the one who did not yet know what he did not know. He keeps the system observable, because you cannot fix what you cannot see. He would rather ship something boring and bulletproof than clever and fragile. He treats uptime as a promise to real people, not a number on a dashboard. He believes clean architecture is what lets a team move fast for years, not weeks. He fell in love with backends the first time a system he built handled more traffic than he expected and simply held. He is fluent across languages and runtimes, and he picks tools for the problem rather than the trend.",
      "He has debugged production at every hour of the day and has the instincts to prove it. He writes migrations like a surgeon — carefully, reversibly, and with a plan for when it goes wrong. He believes observability is a feature you build for your future self at three in the morning. He designs APIs he would actually want to consume, with names that explain themselves. He treats every external dependency as a risk to be understood, not a gift to be trusted. He is unbothered by hype and deeply interested in fundamentals that do not change. He keeps a mental catalog of failure modes and quietly designs around them. He believes the best compliment a backend can get is that nobody ever mentions it. He is generous with his knowledge and ruthless with his own shortcuts. He is the reason the team can promise reliability and actually mean it. If the product feels fast and just works, that is Fahim doing his job well enough that you never had to think about him.",
    ].join("\n\n"),
    experience: "6+ Years",
    location: "Dhaka, BD",
    techStack: [
      // Important — core frameworks, languages & databases
      "Node.js",
      "NestJS",
      "Python",
      "Go",
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Redis",
      "Django",
      "FastAPI",
      // Supporting — libraries, infra, tooling
      "Express",
      "PHP",
      "Laravel",
      "GraphQL",
      "Kafka",
      "RabbitMQ",
      "gRPC",
      "AI",
      "RAG",
      "Chatbot",
      "Automation",
      "Docker",
      "Kubernetes",
      "Terraform",
      "Nginx",
      "Bash",
      "Rust",
      "Postman",
      "Figma",
      "C",
      "C++",
    ],
    expertise: ["Backend Architecture", "Databases", "System Design"],
    funFacts: [
      "Can name the time complexity of your code before you finish typing it.",
      "Thinks in schemas, dreams in indexes.",
    ],
    uniqueItems: [
      { label: "APIs Built", value: "200+" },
      { label: "Queries Tuned", value: "5k" },
      { label: "Uptime", value: "99.9%" },
    ],
    socials: [
      {
        platform: "LinkedIn",
        href: "https://www.linkedin.com/in/abdulmozidfahim/",
      },
      {
        platform: "Facebook",
        href: "https://www.facebook.com/abdulmozidfahim",
      },
    ],
    accentColor: "#ff8a5c",
    quote: "A clean schema today saves a rewrite next year.",
    qa: [
      { q: "Boring or clever?", a: "Boring and bulletproof." },
      { q: "Schema first?", a: "Always. The rest follows." },
      { q: "SQL or NoSQL?", a: "Whichever the data actually needs." },
      { q: "Best backend compliment?", a: "Nobody ever mentions it." },
      { q: "3am outage?", a: "Read the logs, not the panic." },
    ],
    endorsements: [
      {
        by: "Muntahi Safwan",
        role: "Founder",
        quote:
          "If the product just works and nobody's panicking at 3am, that's Fahim doing his job too well to notice.",
      },
      {
        by: "Meraz Uddin",
        role: "DevOps",
        quote:
          "He designs schemas like buildings — still standing ten years out. I deploy them without a second thought.",
      },
      {
        by: "Shaqibul Neil",
        role: "Full-Stack",
        quote:
          "He'll name the time complexity of my function before I finish typing it, kindly. Terrifying and helpful.",
      },
    ],
    metadata: {
      title: "Abdul M. Fahim — Backend Maestro & CTO",
      description:
        "Meet Abdul M. Fahim, CTO of Octarnal — architecting the databases, APIs and infrastructure behind every product.",
      keywords: ["CTO", "Backend", "System Design", "Octarnal Team"],
    },
  },
  {
    id: "04",
    slug: "meraz-uddin",
    name: "Meraz Uddin",
    designation: "Master of Clouds & Minister of DevOps",
    tagline: "Ship anywhere, break nothing.",
    image: "/assets/images/teams/meraz.webp",
    bio: "Meraz makes deployments boring — in the best way. He owns the cloud, the pipelines and the infrastructure so the rest of us just push code and watch it go live.",
    longBio: [
      "Meraz automates everything he touches, and he touches almost everything eventually. As Octarnal's master of clouds and DevOps, his entire job is to make the scary parts of shipping software feel boring — in the best possible way. He builds the pipelines that turn a merged pull request into a live feature without anyone holding their breath. He treats infrastructure as code, because he trusts a reviewed script far more than a remembered click. From CI/CD to container orchestration to cloud-cost tuning, he keeps the whole machine fast, cheap, and quietly self-healing. He measures his success by how little anyone notices him, because the days people notice DevOps are usually the bad days. He has a deep, almost personal grudge against downtime. He sets up monitoring and alerts not because he expects failure, but because he respects it. He would rather be woken at three in the morning by a warning than at nine by an angry client. He believes if you did something twice by hand, you already owed it a script. He tunes cloud bills the way some people tune engines, and he takes real pleasure in a graph that trends down.",
      "He builds systems that fail gracefully, because he knows everything fails eventually. He is the calm voice during an incident, reading logs while everyone else reads panic. He documents his infrastructure so the team is never held hostage by what only he knows. He automates tasks that honestly take longer to automate than to just do, and he has made peace with the irony. He names his servers after planets and defends the naming scheme with a straight face. He thinks about security, backups, and recovery long before anyone asks him to. He treats reliability as a feature the whole business is built on, not a nice-to-have. He keeps the lights on so the rest of the team can take creative risks without fear. He got into DevOps because he could not stand doing the same manual task twice. He is fluent across the major clouds and picks the right one for the workload, not the résumé.",
      "He treats a runbook as a kindness to whoever is on call next, including his future self. He has strong opinions about logging, and every one of them has saved a debugging session. He believes the goal of good infrastructure is to be invisible right up until you need it. He runs game-days and failure drills so real incidents feel rehearsed instead of terrifying. He is the person who quietly upgraded the thing before it became a vulnerability in the news. He balances cost, speed, and reliability like a chef balances salt — you only notice when it is wrong. He automates his own job away on purpose, then finds a harder problem to solve. He believes backups you have never restored are just wishes with extra steps. He keeps calm during outages because panic has never once fixed a pipeline. He is the reason the team can deploy on a Friday without their stomachs dropping. If Octarnal ships often and sleeps well, it is largely because Meraz built the safety net nobody sees.",
    ].join("\n\n"),
    experience: "5+ Years",
    location: "Dhaka, BD",
    techStack: [
      // Important — core platforms, languages & databases
      "AWS",
      "Kubernetes",
      "Docker",
      "Terraform",
      "Linux",
      "GCP",
      "Azure",
      "Python",
      "Go",
      "PostgreSQL",
      // Supporting — tooling, observability, languages
      "Ansible",
      "Jenkins",
      "Helm",
      "ArgoCD",
      "GitHub Actions",
      "Nginx",
      "Bash",
      "Rust",
      "Prometheus",
      "Grafana",
      "Vault",
      "Cloudflare",
      "Redis",
      "Vercel",
      "Netlify",
      "Postman",
      "Figma",
      "C",
      "C++",
    ],
    expertise: ["Cloud Infrastructure", "CI/CD", "Site Reliability"],
    funFacts: [
      "Has automated tasks that take longer to automate than to just do.",
      "Names his servers after planets.",
    ],
    uniqueItems: [
      { label: "Deploys Automated", value: "3k+" },
      { label: "Cloud Regions", value: "12" },
      { label: "Downtime", value: "~0" },
    ],
    socials: [
      {
        platform: "LinkedIn",
        href: "https://www.linkedin.com/in/meraz-uddin003/",
      },
      { platform: "Facebook", href: "https://www.facebook.com/merazuddin003" },
      { platform: "GitHub", href: "https://github.com" },
    ],
    accentColor: "#f5c542",
    quote: "If you did it twice by hand, you should have scripted it.",
    qa: [
      { q: "Manual or scripted?", a: "Did it twice? Script it." },
      { q: "Server names?", a: "Planets. Non-negotiable." },
      { q: "Cloud of choice?", a: "The one the workload wants." },
      { q: "Downtime?", a: "A personal grudge." },
      { q: "Friday deploy?", a: "Sure — the net's already up." },
    ],
    endorsements: [
      {
        by: "Abdul M. Fahim",
        role: "CTO",
        quote:
          "Meraz makes deployments boring, which is the highest compliment infrastructure can earn.",
      },
      {
        by: "Sagor Hossain",
        role: "Co-Founder",
        quote:
          "We ship on Fridays without our stomachs dropping. That's entirely his safety net.",
      },
      {
        by: "Muntahi Safwan",
        role: "Founder",
        quote:
          "He automated his own job, then went looking for a harder problem. Every team needs one.",
      },
    ],
    metadata: {
      title: "Meraz Uddin — Master of Clouds & DevOps",
      description:
        "Meet Meraz Uddin, DevOps and cloud lead at Octarnal — automating the pipelines and infrastructure that keep everything online.",
      keywords: ["DevOps", "Cloud", "Infrastructure", "Octarnal Team"],
    },
  },
  {
    id: "05",
    slug: "mahfuzul-hasan",
    name: "Mahfujul Hasan",
    designation: "Rising Frontend Wizard & Guardian of the UI",
    tagline: "Learning fast, shipping faster.",
    image: "/assets/images/teams/mahfuz.jpg",
    bio: "Mahfujul is our rising frontend talent — turning designs into crisp, responsive interfaces and picking up the full stack along the way. Hand him a Figma file and he'll hand you a live page.",
    longBio: [
      "Mahfujul is early in his journey, but he moves like someone who has been doing this for years and simply forgot to be intimidated. He is our future frontend wizard — the emphasis on 'future' shrinking a little every single week. He builds interfaces with React and Next.js, wires them to real backends, and is never satisfied leaving something at 'good enough'. He asks the kind of questions that make the whole team sharper, the ones more experienced developers stopped asking because they assumed they knew. He learns fast, breaks things in safe places, and turns every mistake into a lesson he does not repeat. He has that rare, valuable hunger — the drive to understand not just how something works, but why it was built that way. He keeps a folder of CSS tricks he is 'definitely going to use someday', and occasionally he actually does. He debugs by explaining the problem out loud to his rubber duck, and it works more often than he would like to admit. He is not afraid to say 'I don't know yet', which is exactly why he is growing so quickly. He treats feedback as fuel rather than criticism, and he comes back stronger after every review. He is the kind of junior who will quietly be a senior before anyone remembers to update his title.",
      "He cares about the small details — the spacing, the easing, the hover state nobody asked about but everybody feels. He is proof that potential, paired with effort, beats experience that stopped trying. And then there are the cats. Mahfujul loves cats with a devotion that borders on a superpower, which is why the team crowned him Octarnal's very own Catman — our homegrown, Marvel-grade superhero whose entire origin story is 'liked cats a normal amount, then kept going'. He genuinely debugs faster when a cat is judging him from the corner of the desk. He will interrupt a serious technical conversation to show you a cat photo, and somehow the code is better afterward. He believes a good day has three ingredients: clean code, working coffee, and at least one cat nearby. He picked up his first framework by rebuilding apps he admired until they stopped feeling like magic and started feeling like code. He is not precious about his work; he will throw away a whole component to learn a better way to build it. He reads other people's pull requests to steal good habits, and he is honest about where he got them.",
      "He treats every unfamiliar bug as a door to something he did not know yesterday. He is the first to volunteer for the task nobody else wants, because that is where the fastest learning hides. He keeps a personal changelog of concepts he has finally understood, and it is growing quickly. He asks 'why' one more time than is comfortable, which is exactly how he catches up so fast. He is building the habits now — testing, accessibility, clean commits — that most people learn the hard way later. He believes talent is just interest that refused to quit, and his interest has not quit yet. He is the kind of teammate whose questions accidentally improve everyone else's understanding. He treats each project as a level to clear, and he rarely leaves without the bonus. And through all of it, there is a cat somewhere nearby, supervising — because a Catman never truly works alone. Give him a year, a keyboard, and a cat, and the 'future' in his title will be long gone — but the Catman part is forever.",
    ].join("\n\n"),
    experience: "1 Year",
    location: "Barishal, BD",
    techStack: [
      // Important — core frameworks, languages & databases
      "React",
      "Next.js",
      "TypeScript",
      "JavaScript",
      "React Native",
      "Node.js",
      "PostgreSQL",
      "MongoDB",
      "Prisma",
      // Supporting — libraries, styling, tooling
      "Express",
      "Tailwind",
      "Framer Motion",
      "GSAP",
      "Socket.io",
      "shadcn",
      "TanStack Table",
      "TanStack Query",
      "Zustand",
      "Redux",
      "REST API",
      "HTML",
      "CSS",
      "Vercel",
      "Netlify",
      "Firebase",
      "Postman",
      "Figma",
    ],
    expertise: ["Frontend Development", "Full-Stack", "UI Engineering"],
    funFacts: [
      "Octarnal's very own Catman — our Marvel-grade superhero, powered entirely by cats.",
      "Debugs twice as fast when a cat is judging him from the desk.",
    ],
    uniqueItems: [
      { label: "Projects Shipped", value: "15+" },
      { label: "Bugs Squashed", value: "600+" },
      { label: "Cups of Chai", value: "∞" },
    ],
    socials: [
      {
        platform: "LinkedIn",
        href: "https://www.linkedin.com/in/mahfujul-hasan-dev/",
      },
      { platform: "Facebook", href: "https://www.facebook.com/mahfujul.sirat" },
    ],
    accentColor: "#5cc6a6",
    quote: "Every bug is just a lesson wearing a disguise.",
    qa: [
      { q: "Cats or code?", a: "Both, ideally at once." },
      { q: "Tabs or spaces?", a: "Spaces, and I'll fight nicely." },
      { q: "Stuck bug?", a: "Explain it to the cat. Works." },
      { q: "Junior or senior?", a: "Junior who forgot to be intimidated." },
      { q: "Best day?", a: "Clean code, coffee, a nearby cat." },
    ],
    endorsements: [
      {
        by: "Shaqibul Neil",
        role: "Full-Stack",
        quote:
          "Catman notices the layout shift you didn't, then can't un-notice it. Same disease as me.",
      },
      {
        by: "Abdul M. Fahim",
        role: "CTO",
        quote:
          "He asks 'why' one more time than is comfortable — which is exactly how he catches up so fast.",
      },
      {
        by: "Muntahi Safwan",
        role: "Founder",
        quote:
          "Hired him for hunger, not pedigree. The 'future' in his title is shrinking every week.",
      },
    ],
    metadata: {
      title: "Mahfujul Hasan — Future Frontend Wizard",
      description:
        "Meet Mahfujul Hasan, rising frontend developer at Octarnal — turning designs into responsive interfaces and growing into the full stack.",
      keywords: ["Frontend", "React", "Next.js", "Octarnal Team"],
    },
  },
  {
    id: "06",
    slug: "shaqibul-neil",
    name: "Shaqibul Neil",
    designation: "Full Stack-ish Developer & Minister of One More Refactor",
    tagline: "Slightly full, mostly stack.",
    image: "/assets/images/teams/shaqibul.webp",
    bio: "Shaqibul works up and down the stack — building interfaces, wiring APIs, and everything in between. The '-ish' is just modesty; he ships across the whole thing.",
    longBio: [
      "Shaqibul is a full-stack developer who is happiest when a feature is entirely his — from the database schema all the way to the last hover animation. He does not like handing off half a problem; he wants the whole thing, end to end, so nothing gets lost in translation. He cares about clean code, smooth motion, and shipping things that do not quietly fall apart three weeks after launch. He is a self-confessed perfectionist with a mild case of code-OCD, and he has stopped pretending otherwise. If a detail is even slightly off — a misaligned pixel, an awkward transition, a function that could be clearer — he will rewrite the same thing again and again until it clears his own stubbornly high bar. This drives him a little crazy and makes his work noticeably better, usually in that order. He genuinely cannot leave 'good enough' alone if he can see the 'great' from where he is standing. He often dreams up the next feature before the current one has even shipped, sometimes literally — ideas arriving at inconvenient hours that he scribbles down before they escape. He names his variables better than he names his projects, and he is aware of the imbalance. He will refactor perfectly working code simply because it 'felt off', and he considers that a virtue, not a bug. He is a fast learner and an even faster typist, endlessly curious about how things actually work under the hood.",
      "He treats motion as part of the interface, not decoration, and he sweats the easing curves nobody consciously notices. He believes accessible, fast, and beautiful is not a menu you pick one from, but a standard you meet all three of. He is the kind of developer who reads the docs, then reads the source, then forms an opinion. He would rather understand one thing deeply than skim ten. He keeps a growing list of side projects, most of them born from a feature idea he could not stop thinking about. He is happiest in that flow state where the schema, the API, and the animation all finally agree with each other. He owns his bugs out loud, because he would rather learn in public than hide a mistake. Full-stack, to him, means full responsibility — and he is genuinely okay with that. He got into full-stack work because he hated the feeling of a problem being someone else's half. He is the developer who notices the layout shift you did not, and then cannot un-notice it.",
      "He keeps a notes app full of feature ideas timestamped at hours no reasonable person is awake. He treats a code review as a conversation with his future self, so he writes for the person who has to read it later. He has strong, quietly held opinions about naming, spacing, and where the logic should live. He will happily spend an hour making something feel right that most people would not notice in a second. He believes a smooth interaction is a form of respect for the person using it. He is teaching himself to know when 'done' is actually done, which is harder for him than any framework. He learns a new tool by building something real and slightly too ambitious with it. He is honest about what he does not know yet, and relentless about not knowing it for long. He treats motion, performance, and accessibility as one problem, not three checkboxes. The OCD that makes him rebuild things five times is the same instinct that makes his fifth version worth shipping. If a feature feels considered down to the last detail, there is a good chance Shaqibul rebuilt it more times than anyone will ever know.",
    ].join("\n\n"),
    experience: "1 Year",
    location: "Dhaka, BD",
    techStack: [
      // Important — core frameworks, languages & databases
      "React",
      "Next.js",
      "TypeScript",
      "JavaScript",
      "React Native",
      "Node.js",
      "NestJS",
      "PostgreSQL",
      "MongoDB",
      "Prisma",
      // Supporting — libraries, styling, tooling
      "Express",
      "Tailwind",
      "Framer Motion",
      "GSAP",
      "Socket.io",
      "TanStack Start",
      "TanStack Table",
      "TanStack Query",
      "shadcn",
      "Zustand",
      "Redux",
      "REST API",
      "HTML",
      "CSS",
      "Vercel",
      "Netlify",
      "Firebase",
      "Postman",
      "Figma",
    ],
    expertise: [
      "Full-Stack Development",
      "Frontend Engineering",
      "Motion & UI",
    ],
    funFacts: [
      "Will refactor working code just because it 'felt off'.",
      "Names variables better than he names his projects.",
      "Often dreams about features to build — then wakes up and builds them.",
    ],
    uniqueItems: [
      { label: "Features Shipped", value: "300+" },
      { label: "Late-Night Commits", value: "1500+" },
      { label: "Side Projects", value: "10" },
    ],
    socials: [
      {
        platform: "LinkedIn",
        href: "https://www.linkedin.com/in/shaqibul-neil/",
      },
      { platform: "Facebook", href: "https://www.facebook.com/shaqibul.neil/" },
    ],
    accentColor: "#00FFFF",
    quote: "Full-stack means full-blame — and I'm okay with that.",
    qa: [
      { q: "Good enough?", a: "Not if I can see 'great' from here." },
      { q: "Frontend or backend?", a: "The whole feature or none of it." },
      { q: "Refactor working code?", a: "If it felt off, yes. Virtue." },
      { q: "Is motion decoration?", a: "No. It's part of the interface." },
      { q: "Feature ideas?", a: "They arrive at 3am. I write them down." },
    ],
    endorsements: [
      {
        by: "Mahfujul Hasan",
        role: "Frontend",
        quote:
          "He'll rebuild a working component five times because it 'felt off'. The fifth one is always worth shipping.",
      },
      {
        by: "Abdul M. Fahim",
        role: "CTO",
        quote:
          "Wants the whole feature — schema to hover state. I trust him with the parts nobody sees.",
      },
      {
        by: "Muntahi Safwan",
        role: "Founder",
        quote:
          "Owns his bugs out loud. That's rarer and more valuable than never having them.",
      },
    ],
    metadata: {
      title: "Shaqibul Neil — Full Stack-ish Developer",
      description:
        "Meet Shaqibul Neil, full-stack developer at Octarnal — building features end to end, from database schema to hover animation.",
      keywords: ["Full-Stack", "Frontend", "Next.js", "Octarnal Team"],
    },
  },
];
